import { JSONSchema7 } from "json-schema";
import {
  emptySchema,
  jsonSchemaGetExamples,
} from "../helpers/jsonShema.helpers";

export type PerVideoDataDocument = {
  schema: JSONSchema7;
  schemaString: string;
  data: unknown;
};

const cloneJsonValue = <T>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));

const parseSchemaInput = (
  schema: JSONSchema7 | string | null | undefined
): JSONSchema7 => {
  if (!schema) {
    return cloneJsonValue(emptySchema) as JSONSchema7;
  }

  if (typeof schema === "string") {
    return JSON.parse(schema || "{}") as JSONSchema7;
  }

  return cloneJsonValue(schema);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const isJsonSchemaObject = (value: unknown): value is JSONSchema7 =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const normalizeDataForSchema = (
  schema: JSONSchema7,
  data: unknown
): unknown => {
  if (schema.properties && typeof schema.properties === "object") {
    const source = isPlainObject(data) ? data : {};

    return Object.entries(schema.properties).reduce<Record<string, unknown>>(
      (result, [key, fieldSchema]) => {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          result[key] = isJsonSchemaObject(fieldSchema)
            ? normalizeDataForSchema(fieldSchema, source[key])
            : cloneJsonValue(source[key]);
        }

        return result;
      },
      {}
    );
  }

  if (isJsonSchemaObject(schema.items) && Array.isArray(data)) {
    return data.map((item) => normalizeDataForSchema(schema.items, item));
  }

  return cloneJsonValue(data);
};

export const createPerVideoDataDocument = (
  schema: JSONSchema7 | string | null | undefined,
  data?: unknown
): PerVideoDataDocument => {
  const schemaCopy = parseSchemaInput(schema);
  const sourceData =
    typeof data === "undefined" ? jsonSchemaGetExamples(schemaCopy) : data;
  const normalizedData = normalizeDataForSchema(schemaCopy, sourceData ?? {});
  schemaCopy.examples = [normalizedData as any];

  return {
    schema: schemaCopy,
    schemaString: JSON.stringify(schemaCopy),
    data: normalizedData,
  };
};

export const getPerVideoDataFromSchema = (
  schema: JSONSchema7 | string | null | undefined
): unknown => createPerVideoDataDocument(schema).data;

export const addPerVideoFieldToDocument = ({
  currentSchema,
  key,
  fieldSchema,
  defaultValue,
}: {
  currentSchema: JSONSchema7 | string | null | undefined;
  key: string;
  fieldSchema: JSONSchema7;
  defaultValue: unknown;
}): PerVideoDataDocument => {
  const currentDocument = createPerVideoDataDocument(currentSchema);
  const data = isPlainObject(currentDocument.data)
    ? cloneJsonValue(currentDocument.data)
    : {};

  currentDocument.schema.properties = {
    ...(currentDocument.schema.properties || {}),
  };
  currentDocument.schema.properties[key] = cloneJsonValue(fieldSchema);
  data[key] = cloneJsonValue(defaultValue);

  return createPerVideoDataDocument(currentDocument.schema, data);
};
